<template>
    <div class="chat-simulator">
        <div class="chat-header">
            <div class="chat-header-content">
                <i class="fas fa-robot"></i>
                <span class="chat-title">{{ chatgptName || 'AI Chat Simulator' }}</span>
            </div>
            <div class="chat-header-actions">
                <button @click="toggleSidebar" class="header-btn" title="Summaries & Threads">
                    <i class="fas fa-list"></i>
                </button>
                <button @click="clearChat" class="header-btn" title="Clear Chat">
                    <i class="fas fa-trash"></i>
                </button>
            </div>
        </div>
        <div class="chat-container">
            <div class="chat-messages" ref="messagesContainer">
            <div v-if="messages.length === 0" class="empty-state">
                <i class="fas fa-comments fa-3x mb-3 text-muted"></i>
                <p class="text-muted">Mulai percakapan dengan AI</p>
            </div>
            <div v-for="(message, index) in messages" :key="index" 
                 :class="['message', message.type]">
                <div class="message-bubble" :class="message.type">
                    <div v-if="message.type === 'user'" class="message-content">
                        <div v-if="message.media_url && message.media_url.length" class="message-media mb-2">
                            <div v-for="(url, idx) in message.media_url" :key="idx" class="message-media-item">
                                <img :src="url" class="message-media-image" alt="User upload" />
                            </div>
                        </div>
                        <div v-if="message.text">{{ message.text }}</div>
                    </div>
                    <div v-else class="message-content">
                        <div v-if="message.loading" class="typing-indicator">
                            <span></span>
                            <span></span>
                            <span></span>
                        </div>
                        <template v-else>
                            <div v-if="message.text" class="message-text-wa" v-html="formatWhatsAppText(message.text)"></div>
                            <div v-if="message.media_url && message.media_url.length" class="message-media mt-2">
                                <div v-for="(url, idx) in message.media_url" :key="idx" class="message-media-item">
                                    <img
                                        v-if="isImageUrl(url)"
                                        :src="url"
                                        class="message-media-image"
                                        alt="Media"
                                        loading="lazy"
                                    />
                                    <a
                                        v-else
                                        :href="url"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        class="message-media-link"
                                    >{{ url }}</a>
                                </div>
                            </div>
                        </template>
                    </div>
                    <div class="message-meta">
                        <span class="message-time">{{ formatTime(message.timestamp) }}</span>
                        <span v-if="message.latency_ms != null" class="message-latency">Process Time: {{ formatLatency(message.latency_ms) }}</span>
                    </div>
                </div>
            </div>
            </div>
            <!-- Sidebar for Summaries and Threads -->
            <div class="chat-sidebar" :class="{ 'sidebar-open': showSidebar }">
                <div class="sidebar-header">
                    <h6>Memory & Context</h6>
                    <button @click="toggleSidebar" class="close-sidebar">
                        <i class="fas fa-times"></i>
                    </button>
                </div>
                <div class="sidebar-content">
                    <div class="sidebar-tabs">
                        <button 
                            @click="activeTab = 'summaries'" 
                            :class="['tab-btn', { active: activeTab === 'summaries' }]"
                        >
                            <i class="fas fa-file-alt"></i> Summaries
                        </button>
                        <button 
                            @click="activeTab = 'threads'" 
                            :class="['tab-btn', { active: activeTab === 'threads' }]"
                        >
                            <i class="fas fa-comments"></i> Threads
                        </button>
                    </div>
                    <div class="sidebar-body">
                        <div v-if="activeTab === 'summaries'" class="tab-content">
                            <div v-if="summariesLoading" class="loading-state">
                                <i class="fas fa-spinner fa-spin"></i> Loading...
                            </div>
                            <div v-else-if="summaries.length === 0" class="empty-state-small">
                                <i class="fas fa-file-alt fa-2x mb-2"></i>
                                <p>Belum ada summary</p>
                            </div>
                            <div v-else class="summary-list">
                                <div 
                                    v-for="summary in summaries" 
                                    :key="summary.id" 
                                    class="summary-item"
                                >
                                    <div class="summary-header">
                                        <span class="summary-date">{{ formatDate(summary.end_time) }}</span>
                                        <span class="summary-count">{{ summary.message_count }} messages</span>
                                    </div>
                                    <div class="summary-text">{{ summary.summary_text }}</div>
                                    <div v-if="summary.key_points" class="summary-keypoints">
                                        <strong>Key Points:</strong>
                                        <ul>
                                            <li v-for="(point, idx) in parseKeyPoints(summary.key_points)" :key="idx">
                                                {{ point }}
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div v-if="activeTab === 'threads'" class="tab-content">
                            <div v-if="threadsLoading" class="loading-state">
                                <i class="fas fa-spinner fa-spin"></i> Loading...
                            </div>
                            <div v-else-if="threads.length === 0" class="empty-state-small">
                                <i class="fas fa-comments fa-2x mb-2"></i>
                                <p>Belum ada thread</p>
                            </div>
                            <div v-else class="thread-list">
                                <div 
                                    v-for="thread in threads" 
                                    :key="thread.id" 
                                    class="thread-item"
                                >
                                    <div class="thread-header">
                                        <span class="thread-key">{{ thread.thread_key.substring(0, 20) }}...</span>
                                    </div>
                                    <div class="thread-info">
                                        <span><i class="fas fa-message"></i> {{ thread.total_messages }} messages</span>
                                        <span><i class="fas fa-clock"></i> {{ formatDate(thread.last_message_at) }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="chat-input-container">
            <div v-if="selectedImage" class="image-preview-container">
                <div class="preview-wrapper">
                    <img :src="selectedImagePreview" alt="Preview" />
                    <button @click="removeSelectedImage" class="remove-preview">
                        <i class="fas fa-times"></i>
                    </button>
                </div>
            </div>
            <div class="chat-input-wrapper">
                <input 
                    type="file" 
                    ref="fileInput" 
                    style="display: none" 
                    accept="image/*"
                    @change="handleFileChange"
                />
                <button 
                    @click="$refs.fileInput.click()" 
                    class="attachment-button"
                    :disabled="isLoading || selectedImage"
                    title="Lampirkan Gambar"
                >
                    <i class="fas fa-paperclip"></i>
                </button>
                <input 
                    v-model="inputMessage" 
                    @keyup.enter="handleEnterKey"
                    @keydown.enter.prevent
                    type="text" 
                    class="chat-input" 
                    placeholder="Ketik pesan..."
                    :disabled="isLoading"
                />
                <button 
                    @click="sendMessage" 
                    class="send-button"
                    :disabled="(!inputMessage.trim() && !selectedImage) || isLoading"
                >
                    <i v-if="isLoading" class="fas fa-spinner fa-spin"></i>
                    <i v-else class="fas fa-paper-plane"></i>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'ChatSimulator',
    props: {
        chatgptId: {
            type: [String, Number],
            required: true
        },
        chatgptName: {
            type: String,
            default: ''
        }
    },
    data() {
        return {
            messages: [],
            inputMessage: '',
            isLoading: false,
            showSidebar: false,
            activeTab: 'summaries',
            summaries: [],
            threads: [],
            summariesLoading: false,
            threadsLoading: false,
            testNumber: 'TEST_' + Date.now(),
            selectedImage: null,
            selectedImagePreview: null,
            isUploading: false
        };
    },
    mounted() {
        // Load saved chat from localStorage
        this.loadChatHistory();
        
        // If no saved chat, add welcome message
        if (this.messages.length === 0) {
            this.addMessage('assistant', 'Halo! Saya AI assistant. Silakan tanyakan sesuatu kepada saya.');
        }
        
        // Scroll ke bawah saat dibuka
        this.$nextTick(() => {
            this.scrollToBottom();
        });
        
        // Load summaries and threads
        this.loadSummaries();
        this.loadThreads();
    },
    methods: {
        async sendMessage() {
            if ((!this.inputMessage.trim() && !this.selectedImage) || this.isLoading) {
                return;
            }

            let fileUrl = null;
            const userMessage = this.inputMessage.trim();
            const currentSelectedImage = this.selectedImage;
            const currentSelectedPreview = this.selectedImagePreview;

            this.inputMessage = '';
            this.removeSelectedImage();

            // Add user message (show image if exists)
            this.addMessage('user', userMessage, false, {
                media_url: currentSelectedPreview ? [currentSelectedPreview] : []
            });

            // Add loading message
            const loadingIndex = this.addMessage('assistant', '', true);

            // Scroll to bottom
            this.$nextTick(() => {
                this.scrollToBottom();
            });

            try {
                this.isLoading = true;
                
                // Upload image if selected
                if (currentSelectedImage) {
                    const formData = new FormData();
                    formData.append('file', currentSelectedImage);
                    
                    const uploadResp = await this.$store.dispatch('cdn_upload', formData);
                    if (uploadResp.data.success) {
                        fileUrl = uploadResp.data.data.url;
                    } else {
                        throw new Error(uploadResp.data.message || 'Gagal upload gambar');
                    }
                }

                const response = await this.$store.dispatch('chatgpt_test', {
                    id: this.chatgptId,
                    message: userMessage,
                    number: this.testNumber,
                    file: fileUrl
                });

                // Update loading message with actual response
                if (response.data.success) {
                    const data = response.data.data;
                    this.messages[loadingIndex].text = data.answer || 'Tidak ada respons dari AI';
                    this.messages[loadingIndex].loading = false;
                    this.messages[loadingIndex].response_type = data.response_type || 'text';
                    this.messages[loadingIndex].media_url = data.media_url || [];
                    this.messages[loadingIndex].latency_ms = data.latency_ms != null ? data.latency_ms : null;
                    // Save to localStorage after response
                    this.saveChatHistory();
                } else {
                    this.messages[loadingIndex].text = 'Error: ' + (response.data.message || 'Gagal mendapatkan respons');
                    this.messages[loadingIndex].loading = false;
                    this.saveChatHistory();
                }
            } catch (error) {
                console.error('Error sending message:', error);
                this.messages[loadingIndex].text = 'Error: Gagal mengirim pesan. Silakan coba lagi.';
                this.messages[loadingIndex].loading = false;
                this.saveChatHistory();
            } finally {
                this.isLoading = false;
                this.$nextTick(() => {
                    this.scrollToBottom();
                });
            }
        },
        addMessage(type, text, loading = false, options = {}) {
            const message = {
                type: type,
                text: text,
                timestamp: Date.now(),
                loading: loading,
                response_type: options.response_type,
                media_url: options.media_url || [],
                latency_ms: options.latency_ms != null ? options.latency_ms : null
            };
            this.messages.push(message);
            // Don't save here - will be saved by watcher or after response
            return this.messages.length - 1;
        },
        scrollToBottom() {
            const container = this.$refs.messagesContainer;
            if (container) {
                container.scrollTop = container.scrollHeight;
            }
        },
        formatTime(timestamp) {
            const date = new Date(timestamp);
            return date.toLocaleTimeString('id-ID', { 
                hour: '2-digit', 
                minute: '2-digit' 
            });
        },
        formatLatency(ms) {
            if (ms == null) return '';
            if (ms >= 1000) return (ms / 1000).toFixed(1) + 's';
            return ms + 'ms';
        },
        handleEnterKey() {
            this.sendMessage();
        },
        handleFileChange(event) {
            const file = event.target.files[0];
            if (!file) return;

            if (!file.type.startsWith('image/')) {
                alert('Hanya file gambar yang diperbolehkan');
                return;
            }

            if (file.size > 5 * 1024 * 1024) { // 5MB limit
                alert('Ukuran file maksimal 5MB');
                return;
            }

            this.selectedImage = file;
            this.selectedImagePreview = URL.createObjectURL(file);
        },
        removeSelectedImage() {
            this.selectedImage = null;
            if (this.selectedImagePreview) {
                URL.revokeObjectURL(this.selectedImagePreview);
                this.selectedImagePreview = null;
            }
            if (this.$refs.fileInput) {
                this.$refs.fileInput.value = '';
            }
        },
        formatDate(timestamp) {
            const date = new Date(timestamp);
            return date.toLocaleString('id-ID', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        },
        parseKeyPoints(keyPointsStr) {
            try {
                return JSON.parse(keyPointsStr);
            } catch (e) {
                return [];
            }
        },
        isImageUrl(url) {
            if (!url || typeof url !== 'string') return false;
            const u = url.toLowerCase().split('?')[0];
            return /\.(png|jpe?g|gif|webp|bmp)(\?|$)/i.test(u) || u.includes('/cdn/');
        },
        formatWhatsAppText(text) {
            if (!text || typeof text !== 'string') return '';
            const escape = (s) => String(s)
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;');
            let out = escape(text);
            out = out.replace(/\n/g, '<br>');
            out = out.replace(/```([^`]*)```/g, '<code class="wa-monospace">$1</code>');
            out = out.replace(/\*([^*]*)\*/g, '<strong class="wa-bold">$1</strong>');
            out = out.replace(/_([^_]*)_/g, '<em class="wa-italic">$1</em>');
            out = out.replace(/~([^~]*)~/g, '<s class="wa-strike">$1</s>');
            return out;
        },
        saveChatHistory() {
            const storageKey = `chatgpt_test_${this.chatgptId}`;
            localStorage.setItem(storageKey, JSON.stringify({
                messages: this.messages,
                testNumber: this.testNumber,
                lastUpdated: Date.now()
            }));
        },
        loadChatHistory() {
            const storageKey = `chatgpt_test_${this.chatgptId}`;
            const saved = localStorage.getItem(storageKey);
            if (saved) {
                try {
                    const data = JSON.parse(saved);
                    this.messages = data.messages || [];
                    this.testNumber = data.testNumber || this.testNumber;
                } catch (e) {
                    console.error('Error loading chat history:', e);
                }
            }
        },
        clearChat() {
            if (confirm('Apakah Anda yakin ingin menghapus semua chat?')) {
                this.messages = [];
                const storageKey = `chatgpt_test_${this.chatgptId}`;
                localStorage.removeItem(storageKey);
                this.addMessage('assistant', 'Chat telah dihapus. Mulai percakapan baru!');
            }
        },
        toggleSidebar() {
            this.showSidebar = !this.showSidebar;
            if (this.showSidebar) {
                this.loadSummaries();
                this.loadThreads();
            }
        },
        async loadSummaries() {
            this.summariesLoading = true;
            try {
                const response = await this.$store.dispatch('chatgpt_summaries', {
                    id: this.chatgptId,
                    number: this.testNumber
                });
                if (response.data.success) {
                    this.summaries = response.data.data.summaries || [];
                }
            } catch (error) {
                console.error('Error loading summaries:', error);
            } finally {
                this.summariesLoading = false;
            }
        },
        async loadThreads() {
            this.threadsLoading = true;
            try {
                const response = await this.$store.dispatch('chatgpt_threads', {
                    id: this.chatgptId,
                    number: this.testNumber
                });
                if (response.data.success) {
                    this.threads = response.data.data.threads || [];
                }
            } catch (error) {
                console.error('Error loading threads:', error);
            } finally {
                this.threadsLoading = false;
            }
        }
    },
    watch: {
        messages: {
            handler() {
                this.$nextTick(() => {
                    this.scrollToBottom();
                });
                // Auto-save when messages change
                this.saveChatHistory();
            },
            deep: true
        }
    }
};
</script>

<style scoped>
.chat-simulator {
    display: flex;
    flex-direction: column;
    height: 600px;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    overflow: hidden;
    background: #f5f5f5;
}

.chat-header {
    background: #667eea;
    color: white;
    padding: 15px 20px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.chat-header-actions {
    display: flex;
    gap: 10px;
}

.header-btn {
    background: rgba(255, 255, 255, 0.2);
    border: none;
    color: white;
    width: 35px;
    height: 35px;
    border-radius: 50%;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.2s;
}

.header-btn:hover {
    background: rgba(255, 255, 255, 0.3);
}

.chat-container {
    display: flex;
    flex: 1;
    position: relative;
    overflow: hidden;
}

.chat-header-content {
    display: flex;
    align-items: center;
    gap: 10px;
}

.chat-header-content i {
    font-size: 20px;
}

.chat-title {
    font-weight: 600;
    font-size: 16px;
}

.chat-messages {
    flex: 1;
    overflow-y: auto;
    padding: 20px;
    background: #ece5dd;
    background-image: 
        repeating-linear-gradient(
            45deg,
            transparent,
            transparent 10px,
            rgba(0,0,0,.03) 10px,
            rgba(0,0,0,.03) 20px
        );
    transition: margin-right 0.3s;
}

.chat-messages.sidebar-open {
    margin-right: 300px;
}

.chat-sidebar {
    position: absolute;
    right: -300px;
    top: 0;
    bottom: 0;
    width: 300px;
    background: white;
    border-left: 1px solid #e0e0e0;
    display: flex;
    flex-direction: column;
    transition: right 0.3s;
    z-index: 10;
    box-shadow: -2px 0 8px rgba(0,0,0,0.1);
}

.chat-sidebar.sidebar-open {
    right: 0;
}

.sidebar-header {
    padding: 15px;
    border-bottom: 1px solid #e0e0e0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: #f8f9fa;
}

.sidebar-header h6 {
    margin: 0;
    font-weight: 600;
}

.close-sidebar {
    background: none;
    border: none;
    cursor: pointer;
    color: #666;
    font-size: 18px;
}

.close-sidebar:hover {
    color: #333;
}

.sidebar-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.sidebar-tabs {
    display: flex;
    border-bottom: 1px solid #e0e0e0;
}

.tab-btn {
    flex: 1;
    padding: 12px;
    border: none;
    background: white;
    cursor: pointer;
    border-bottom: 2px solid transparent;
    transition: all 0.2s;
    font-size: 13px;
}

.tab-btn:hover {
    background: #f8f9fa;
}

.tab-btn.active {
    border-bottom-color: #667eea;
    color: #667eea;
    font-weight: 600;
}

.tab-btn i {
    margin-right: 5px;
}

.sidebar-body {
    flex: 1;
    overflow-y: auto;
    padding: 15px;
}

.loading-state {
    text-align: center;
    padding: 20px;
    color: #999;
}

.empty-state-small {
    text-align: center;
    padding: 30px 15px;
    color: #999;
}

.empty-state-small i {
    display: block;
    margin-bottom: 10px;
}

.summary-list, .thread-list {
    display: flex;
    flex-direction: column;
    gap: 15px;
}

.summary-item, .thread-item {
    padding: 12px;
    background: #f8f9fa;
    border-radius: 8px;
    border: 1px solid #e0e0e0;
}

.summary-header, .thread-header {
    display: flex;
    justify-content: space-between;
    margin-bottom: 8px;
    font-size: 12px;
    color: #666;
}

.summary-date, .thread-key {
    font-weight: 600;
}

.summary-count {
    color: #999;
}

.summary-text {
    font-size: 13px;
    line-height: 1.5;
    margin-bottom: 8px;
    color: #333;
}

.summary-keypoints {
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px solid #e0e0e0;
    font-size: 12px;
}

.summary-keypoints strong {
    display: block;
    margin-bottom: 5px;
    color: #667eea;
}

.summary-keypoints ul {
    margin: 5px 0 0 0;
    padding-left: 20px;
}

.summary-keypoints li {
    margin-bottom: 3px;
    color: #666;
}

.thread-info {
    display: flex;
    flex-direction: column;
    gap: 5px;
    font-size: 12px;
    color: #666;
}

.thread-info span {
    display: flex;
    align-items: center;
    gap: 5px;
}

.thread-info i {
    font-size: 10px;
}

.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    color: #999;
}

.message {
    display: flex;
    margin-bottom: 15px;
    animation: fadeIn 0.3s;
}

.message.user {
    justify-content: flex-end;
}

.message.assistant {
    justify-content: flex-start;
}

.message-bubble {
    max-width: 70%;
    padding: 10px 15px;
    border-radius: 18px;
    position: relative;
    box-shadow: 0 1px 2px rgba(0,0,0,0.1);
}

.message-bubble.user {
    background: #dcf8c6;
    border-bottom-right-radius: 4px;
}

.message-bubble.assistant {
    background: white;
    border-bottom-left-radius: 4px;
}

.message-content {
    margin-bottom: 5px;
    line-height: 1.4;
    white-space: pre-line;
}

.message-text-wa {
    white-space: pre-line;
}

.message-text-wa .wa-bold {
    font-weight: 700;
}

.message-text-wa .wa-italic {
    font-style: italic;
}

.message-text-wa .wa-strike {
    text-decoration: line-through;
}

.message-text-wa .wa-monospace {
    font-family: monospace;
    font-size: 0.95em;
    background: rgba(0, 0, 0, 0.06);
    padding: 1px 4px;
    border-radius: 4px;
}

.message-media {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.message-media-image {
    max-width: 100%;
    max-height: 280px;
    border-radius: 8px;
    object-fit: contain;
    display: block;
}

.message-media-link {
    font-size: 13px;
    color: #007bff;
    word-break: break-all;
}

.message-meta {
    margin-top: 5px;
    font-size: 11px;
    color: #999;
    text-align: right;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
}

.message-bubble.assistant .message-meta {
    justify-content: flex-start;
    text-align: left;
}

.message-latency {
    color: #6c757d;
    font-size: 10px;
}

.typing-indicator {
    display: flex;
    gap: 4px;
    padding: 5px 0;
}

.typing-indicator span {
    height: 8px;
    width: 8px;
    background: #999;
    border-radius: 50%;
    display: inline-block;
    animation: typing 1.4s infinite;
}

.typing-indicator span:nth-child(2) {
    animation-delay: 0.2s;
}

.typing-indicator span:nth-child(3) {
    animation-delay: 0.4s;
}

@keyframes typing {
    0%, 60%, 100% {
        transform: translateY(0);
        opacity: 0.7;
    }
    30% {
        transform: translateY(-10px);
        opacity: 1;
    }
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.chat-input-container {
    background: white;
    padding: 15px;
    border-top: 1px solid #e0e0e0;
}

.chat-input-wrapper {
    display: flex;
    gap: 10px;
    align-items: center;
}

.chat-input {
    flex: 1;
    padding: 12px 15px;
    border: 1px solid #e0e0e0;
    border-radius: 25px;
    outline: none;
    font-size: 14px;
}

.chat-input:focus {
    border-color: #667eea;
}

.chat-input:disabled {
    background: #f5f5f5;
    cursor: not-allowed;
}

.send-button {
    width: 45px;
    height: 45px;
    border-radius: 50%;
    background: #667eea;
    color: white;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform 0.2s, opacity 0.2s;
}

.send-button:hover:not(:disabled) {
    transform: scale(1.05);
}

.send-button:active:not(:disabled) {
    transform: scale(0.95);
}

.send-button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.send-button i {
    font-size: 16px;
}

.attachment-button {
    background: none;
    border: none;
    color: #666;
    font-size: 20px;
    cursor: pointer;
    padding: 5px;
    transition: color 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
}

.attachment-button:hover:not(:disabled) {
    color: #667eea;
}

.attachment-button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.image-preview-container {
    padding: 10px;
    background: #f8f9fa;
    border-top: 1px solid #e0e0e0;
    margin-bottom: -1px;
}

.preview-wrapper {
    position: relative;
    display: inline-block;
}

.preview-wrapper img {
    height: 80px;
    width: auto;
    border-radius: 8px;
    border: 1px solid #e0e0e0;
    object-fit: cover;
}

.remove-preview {
    position: absolute;
    top: -8px;
    right: -8px;
    background: #ff4d4f;
    color: white;
    border: none;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    font-size: 10px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 4px rgba(0,0,0,0.2);
}

.remove-preview:hover {
    background: #ff7875;
}

.message-bubble.user .message-content img {
    max-width: 100%;
    max-height: 250px;
    border-radius: 8px;
    margin-bottom: 5px;
    display: block;
}

/* Scrollbar styling */
.chat-messages::-webkit-scrollbar {
    width: 6px;
}

.chat-messages::-webkit-scrollbar-track {
    background: transparent;
}

.chat-messages::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 3px;
}

.chat-messages::-webkit-scrollbar-thumb:hover {
    background: #999;
}
</style>

